import React from "react";
import type { Layer, PathOptions } from "leaflet";
import type * as GeoJSONTypes from "geojson";
import { useMap } from "react-leaflet";
import type { FmStandMapProps } from "./FmStandMap.types";
import PropertyForestMap from "@/components/Map/PropertyForestMap";
import { useOptionalFmMapViewport } from "@/components/ui/FmMapViewportContext";
import { ForestStandGeoJsonLayer } from "@/components/Map/ForestContextLayers";
import { StaticMapInteractionController } from "@/components/Map/MapLayers";
import { FmStandBasemapTiles } from "@/shared/ui/FmStandBasemapTiles";
import { applyMapFit, resolveMapFitBounds } from "@/components/Map/mapFitTarget";
import { BASEMAP_NATIVE_MAX_ZOOM } from "@/shared/config/basemapCoverage";

interface FmStandMapFillHostProps {
  fill: boolean;
  fillHostRef: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
}

export const FmStandMapFillHost: React.FC<FmStandMapFillHostProps> = ({
  fill,
  fillHostRef,
  children,
}) => {
  if (!fill) return children;
  return (
    <div ref={fillHostRef} style={{ height: "100%", width: "100%" }}>
      {children}
    </div>
  );
};

interface FmStandMapBodyProps extends Pick<
  FmStandMapProps,
  | "geojson"
  | "bbox"
  | "borderRadius"
  | "testId"
  | "mapOptions"
  | "defaultCenter"
  | "defaultZoom"
  | "basemap"
  | "basemapTone"
  | "onRasterUnavailableChange"
  | "standPane"
  | "mapChildren"
  | "overlays"
  | "legendPortalTarget"
  | "controlsPortalTarget"
  | "fitFeatureIds"
> {
  viewportPolicy: NonNullable<FmStandMapProps["viewportPolicy"]>;
  viewportIdentity?: string;
  resolvedHeight: number;
  layerKey: string;
  layerStyle: (feature?: GeoJSONTypes.Feature) => PathOptions;
  onEachFeature: (feature: GeoJSONTypes.Feature, layer: Layer) => void;
}

/**
 * The fit control returns to the same frame the first fit drew. A stand map
 * without bounds of its own (the workspace map, whose workspace owns the fit)
 * registers nothing, so it never takes the control from its owner; the latest
 * frame is read through a ref so data refreshes do not re-register it.
 */
const FmStandMapViewportController: React.FC<{
  bbox: [number, number, number, number] | null | undefined;
  geojson: GeoJSONTypes.FeatureCollection | null;
  fitFeatureIds: readonly string[] | undefined;
  fitMaxZoom: number | undefined;
}> = ({ bbox, geojson, fitFeatureIds, fitMaxZoom }) => {
  const map = useMap();
  const register = useOptionalFmMapViewport()?.register;
  const frame = React.useRef({ bbox, geojson, fitFeatureIds, fitMaxZoom });
  frame.current = { bbox, geojson, fitFeatureIds, fitMaxZoom };
  const ownsFit = Boolean(bbox);

  React.useEffect(() => {
    if (!register || !ownsFit) return undefined;
    register({
      fit: () => {
        const current = frame.current;
        const bounds = resolveMapFitBounds(current.bbox, current.geojson, current.fitFeatureIds);
        if (bounds) applyMapFit(map, { key: "control", bounds, maxZoom: current.fitMaxZoom });
      },
      zoomIn: () => map.zoomIn(),
      zoomOut: () => map.zoomOut(),
    });
    return () => register(null);
  }, [map, ownsFit, register]);

  return null;
};

/** The deepest native zoom of the basemap the stand map draws, so fits never upscale tiles. */
function basemapFitMaxZoom(basemap: FmStandMapProps["basemap"]): number | undefined {
  if (basemap === "ortokuva") return BASEMAP_NATIVE_MAX_ZOOM.ortokuva;
  if (basemap === "maastokartta") return BASEMAP_NATIVE_MAX_ZOOM.maastokartta;
  return undefined;
}

export const FmStandMapBody: React.FC<FmStandMapBodyProps> = ({
  geojson,
  bbox,
  borderRadius,
  testId,
  mapOptions,
  defaultCenter,
  defaultZoom,
  basemap,
  basemapTone,
  onRasterUnavailableChange,
  standPane,
  mapChildren,
  overlays,
  legendPortalTarget,
  controlsPortalTarget,
  fitFeatureIds,
  viewportPolicy,
  viewportIdentity,
  resolvedHeight,
  layerKey,
  layerStyle,
  onEachFeature,
}) => (
  <PropertyForestMap
    height={resolvedHeight}
    geojson={geojson}
    bbox={bbox}
    fitBoundsMode={viewportPolicy === "embedded-controlled" ? "initial" : "bounds-change"}
    fitBoundsIdentity={viewportIdentity}
    fitFeatureIds={fitFeatureIds}
    fitMaxZoom={basemapFitMaxZoom(basemap)}
    tileLayer="none"
    border={false}
    borderRadius={borderRadius}
    testId={testId}
    mapOptions={mapOptions}
    defaultCenter={defaultCenter}
    defaultZoom={defaultZoom}
    mapChildren={(state) => (
      <>
        <FmStandMapViewportController
          bbox={state.bbox}
          geojson={state.standsGeoJson}
          fitFeatureIds={fitFeatureIds}
          fitMaxZoom={basemapFitMaxZoom(basemap)}
        />
        {viewportPolicy === "embedded-controlled" ? <StaticMapInteractionController /> : null}
        {basemap !== "none" && <FmStandBasemapTiles layer={basemap} tone={basemapTone} onRasterUnavailableChange={onRasterUnavailableChange} />}
        {state.standsGeoJson && (
          <ForestStandGeoJsonLayer
            data={state.standsGeoJson}
            layerKey={layerKey}
            pane={standPane}
            styleOverride={layerStyle}
            onEachFeature={onEachFeature}
          />
        )}
        {mapChildren?.(state.standsGeoJson)}
      </>
    )}
    overlays={
      <>
        {overlays}
        {legendPortalTarget}
        {controlsPortalTarget}
      </>
    }
  />
);
